import { createContext, useContext, useState, useCallback } from "react";
import { editorApiBase } from "@/lib/editorApi";

interface EditModeContextValue {
  editMode: boolean;
  toggleEditMode: () => void;
  overrides: Record<string, unknown>;
  updateContent: (contentId: string, data: unknown) => void;
  saveContent: (contentId: string) => Promise<void>;
  discardContent: (contentId: string) => void;
  saveStatus: Record<string, "idle" | "saving" | "saved" | "error">;
  activeContentId: string | null;
  setActiveContentId: (id: string | null) => void;
}

const EditModeContext = createContext<EditModeContextValue | null>(null);

export function EditModeProvider({ children }: { children: React.ReactNode }) {
  const [editMode, setEditMode] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});
  const [saveStatus, setSaveStatus] = useState<Record<string, "idle" | "saving" | "saved" | "error">>({});
  const [activeContentId, setActiveContentId] = useState<string | null>(null);

  const toggleEditMode = useCallback(() => {
    setEditMode((prev) => !prev);
  }, []);

  const updateContent = useCallback((contentId: string, data: unknown) => {
    setOverrides((prev) => ({ ...prev, [contentId]: data }));
    setSaveStatus((prev) => ({ ...prev, [contentId]: "idle" }));
  }, []);

  const discardContent = useCallback((contentId: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[contentId];
      return next;
    });
    setSaveStatus((prev) => {
      const next = { ...prev };
      delete next[contentId];
      return next;
    });
  }, []);

  const saveContent = useCallback(async (contentId: string) => {
    const data = overrides[contentId];
    if (!data) return;

    // contentId is like "pages/home" or "retreats/art-of-presence"
    const [type, ...rest] = contentId.split("/");
    const id = rest.join("/");

    setSaveStatus((prev) => ({ ...prev, [contentId]: "saving" }));
    try {
      const res = await fetch(`${editorApiBase()}/content/${type}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Save failed");
      setSaveStatus((prev) => ({ ...prev, [contentId]: "saved" }));
      setTimeout(() => {
        setSaveStatus((prev) => ({ ...prev, [contentId]: "idle" }));
      }, 2000);
    } catch {
      setSaveStatus((prev) => ({ ...prev, [contentId]: "error" }));
    }
  }, [overrides]);

  return (
    <EditModeContext.Provider
      value={{
        editMode,
        toggleEditMode,
        overrides,
        updateContent,
        saveContent,
        discardContent,
        saveStatus,
        activeContentId,
        setActiveContentId,
      }}
    >
      {children}
    </EditModeContext.Provider>
  );
}

const noopEditMode: EditModeContextValue = {
  editMode: false,
  toggleEditMode: () => {},
  overrides: {},
  updateContent: () => {},
  saveContent: async () => {},
  discardContent: () => {},
  saveStatus: {},
  activeContentId: null,
  setActiveContentId: () => {},
};

export function useEditMode() {
  const ctx = useContext(EditModeContext);
  return ctx ?? noopEditMode;
}
